import React from 'react';
import { X, ChevronLeft, ChevronRight, Calendar, CheckCircle, Info, Clock, CreditCard, CheckSquare, FileText } from 'lucide-react';
import useBlockedDates from '../../hooks/useBlockedDates';
import { UIVenue } from '../../utils/venueTransformer';
import { getSecurityDeposit, getGstRate } from '../../utils/venueCharges';

interface TimeSlot {
  startTime: string;
  endTime: string;
  hours: number;
  rate: number;
}

interface AvailabilityModalProps {
  show: boolean;
  onClose: () => void;
  venues: UIVenue[];
  isMobile: boolean;
  theme: any;
  onProceedToPayment: (bookingData: any) => void;
  isProcessingPayment: boolean;
}

export const AvailabilityModal: React.FC<AvailabilityModalProps> = ({
  show,
  onClose,
  venues,
  isMobile,
  theme,
  onProceedToPayment,
  isProcessingPayment
}) => {
  const [currentMonth, setCurrentMonth] = React.useState(new Date());
  const [selectedDates, setSelectedDates] = React.useState<{ from: string; to: string }>({ from: '', to: '' });
  const [selectionMode] = React.useState<'single' | 'range'>('single');
  const [tempHoverDate, setTempHoverDate] = React.useState<string | null>(null);
  const [selectedVenue, setSelectedVenue] = React.useState<UIVenue | null>(null);
  const [termsAccepted, setTermsAccepted] = React.useState<boolean>(false);
const [showTermsModal, setShowTermsModal] = React.useState<boolean>(false);
  
  // Time slot selection state
  const [selectedTimeSlots, setSelectedTimeSlots] = React.useState<TimeSlot[]>([]);
  const [availableStartTimes, setAvailableStartTimes] = React.useState<string[]>([]);
  
  // Form data state
  const [formData, setFormData] = React.useState({
    eventName: '',
    applicantName: '',
    mobile: '',
    email: '',
    gstNumber: ''
  });
  const [formErrors, setFormErrors] = React.useState<any>({
    
  });

  // Use the custom hook to fetch blocked dates
  const {
    blockedDates,
    loading: loadingBlockedDates,
    error: blockedDatesError
  } = useBlockedDates(
    selectedVenue?.id || selectedVenue?.name,
    currentMonth,
    show && !!selectedVenue
  );

  // Reset state when modal closes
  React.useEffect(() => {
    if (!show) {
      setSelectedVenue(null);
      setSelectedDates({ from: '', to: '' });
      setTempHoverDate(null);
      setCurrentMonth(new Date());
      setSelectedTimeSlots([]);
      setFormData({
        eventName: '',
        applicantName: '',
        mobile: '',
        email: '',
        gstNumber: ''
      });
      setFormErrors({});
      setTermsAccepted(false); // ✅ ADD THIS LINE
    setShowTermsModal(false); // ✅ ADD THIS LINE
    }
  }, [show]);

  // Load available time slots when venue with timing slots is selected
  React.useEffect(() => {
    if (selectedVenue?.hasTimingSlots && selectedVenue?.timingSlotConfig) {
      setAvailableStartTimes(selectedVenue.timingSlotConfig.availableStartTimes || []);
    } else {
      setAvailableStartTimes([]);
      setSelectedTimeSlots([]);
    }
  }, [selectedVenue]);

  if (!show) return null;

  const getMonthDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };

  const handleVenueSelect = (venue: UIVenue) => {
    setSelectedVenue(venue);
    setSelectedDates({ from: '', to: '' });
    setSelectedTimeSlots([]);
    setTempHoverDate(null);
    // Scroll to calendar
    setTimeout(() => {
      const modal = document.querySelector('[data-availability-modal]');
      if (modal) {
        modal.scrollTo({ top: modal.scrollHeight, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleDateClick = (year: number, month: number, day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    // Check if date is blocked
    if (blockedDates.has(dateStr)) {
      alert('This date is not available. Please choose another date.');
      return;
    }

    // Check if date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const clickedDate = new Date(year, month, day);
    if (clickedDate < today) {
      alert('Cannot select past dates');
      return;
    }

    // Check if date is beyond 6 months
    const maxBookingDate = new Date();
    maxBookingDate.setMonth(maxBookingDate.getMonth() + 6);
    maxBookingDate.setHours(0, 0, 0, 0);
    if (clickedDate > maxBookingDate) {
      alert('Bookings can only be made up to 6 months in advance');
      return;
    }

    // For shooting venues with time slots, only single day selection
    if (selectedVenue?.hasTimingSlots) {
      setSelectedDates({ from: dateStr, to: dateStr });
      setSelectedTimeSlots([]);
      return;
    }

    // For regular venues, allow single or range mode
    if (selectionMode === 'single') {
      setSelectedDates({ from: dateStr, to: dateStr });
    } else {
      // Range mode logic
      if (!selectedDates.from || selectedDates.to) {
        setSelectedDates({ from: dateStr, to: '' });
        setTempHoverDate(null);
      } else {
        const from = new Date(selectedDates.from);
        const to = new Date(year, month, day);
        
        if (to < from) {
          setSelectedDates({ from: dateStr, to: selectedDates.from });
        } else {
          // Check if any date in range is blocked
          let hasBlockedDate = false;
          const currentDate = new Date(from);
          while (currentDate <= to) {
            const checkStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
            if (blockedDates.has(checkStr)) {
              hasBlockedDate = true;
              break;
            }
            currentDate.setDate(currentDate.getDate() + 1);
          }

          if (hasBlockedDate) {
            alert('Selected range contains blocked dates. Please choose different dates.');
            return;
          }

          setSelectedDates({ from: selectedDates.from, to: dateStr });
        }
        setTempHoverDate(null);
      }
    }
  };

  const handleTimeSlotSelect = (startTime: string) => {
    if (!selectedVenue?.timingSlotConfig) return;

    const config = selectedVenue.timingSlotConfig;
    const endTime = addHoursToTime(startTime, 4); // Default 4-hour slot

    // Calculate rate - ₹4,000 per 4-hour slot
    const rate = config.ratePerFourHours;

    const newSlot: TimeSlot = {
      startTime,
      endTime,
      hours: 4,
      rate
    };

    // Check if slot already selected
    const exists = selectedTimeSlots.some(
      slot => slot.startTime === startTime
    );

    if (exists) {
      // Remove slot (toggle off)
      setSelectedTimeSlots(selectedTimeSlots.filter(s => s.startTime !== startTime));
    } else {
      // Add slot (allow multiple selections)
      setSelectedTimeSlots([...selectedTimeSlots, newSlot]);
    }
  };

  const addHoursToTime = (time: string, hours: number): string => {
    const [h, m] = time.split(':').map(Number);
    const totalMinutes = h * 60 + m + (hours * 60);
    const newHours = Math.floor(totalMinutes / 60);
    const newMinutes = totalMinutes % 60;
    return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
  };

  const isDateSelected = (dateStr: string) => {
    if (!selectedDates.from) return false;
    
    if (selectionMode === 'single' || selectedVenue?.hasTimingSlots) {
      return selectedDates.from === dateStr;
    }

    if (!selectedDates.to) {
      return selectedDates.from === dateStr;
    }

    const date = new Date(dateStr);
    const from = new Date(selectedDates.from);
    const to = new Date(selectedDates.to);
    return date >= from && date <= to;
  };

  const isDateInTempRange = (dateStr: string) => {
    if (!selectedDates.from || !tempHoverDate || selectedDates.to || selectedVenue?.hasTimingSlots) return false;
    
    const date = new Date(dateStr);
    const from = new Date(selectedDates.from);
    const temp = new Date(tempHoverDate);
    
    const start = from < temp ? from : temp;
    const end = from > temp ? from : temp;
    
    return date >= start && date <= end;
  };

  const validateForm = () => {
    const errors: any = {};

    if (!selectedDates.from) {
      errors.from = 'Please select a date';
    }

    if (selectedVenue?.hasTimingSlots && selectedTimeSlots.length === 0) {
      errors.timeSlots = 'Please select at least one time slot';
    }

    if (!formData.eventName?.trim()) {
      errors.eventName = 'Event name is required';
    }

    if (!formData.applicantName?.trim()) {
      errors.applicantName = 'Applicant name is required';
    }

    if (!formData.mobile?.trim()) {
      errors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile.replace(/\D/g, ''))) {
      errors.mobile = 'Please enter a valid 10-digit mobile number';
    }

    if (!formData.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!termsAccepted) {
    errors.termsAccepted = 'You must accept the terms and conditions to proceed';
  }


    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProceedToPayment = () => {
    if (!validateForm()) {
      alert('Please fill in all required fields');
      return;
    }

    if (!validateForm()) {
  alert('Please fill in all required fields and accept the terms and conditions'); // ✅ UPDATE THIS
  return;
}

    if (!selectedVenue || !selectedDates.from) {
      alert('Please select a venue and dates');
      return;
    }

    const costs = calculateTotal();
    const to = selectedDates.to || selectedDates.from;

    // ✅ CRITICAL FIX: Include complete venue object in bookingData
    const bookingData = {
      venueId: selectedVenue.id,
      venue: selectedVenue, // ✅ Pass the entire venue object
      eventName: formData.eventName.trim(),
      applicantName: formData.applicantName.trim(),
      mobile: formData.mobile.replace(/\D/g, ''),
      email: formData.email.trim().toLowerCase(),
      gstNumber: formData.gstNumber?.trim() || '',
      bookingDates: {
        from: selectedDates.from,
        to: to
      },
      // Time slots for shooting venues
      timeSlots: selectedVenue.hasTimingSlots ? selectedTimeSlots : undefined,
      // Regular venue costs
      prefixDays: 0,
      suffixDays: 0,
      costs: costs
    };

    console.log('Proceeding to payment with:', bookingData);
    onProceedToPayment(bookingData);
  };

  const { firstDay, daysInMonth } = selectedVenue 
    ? getMonthDays(currentMonth)
    : { firstDay: 0, daysInMonth: 0 };
  
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  // Restrict calendar to next 6 months only
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const maxDate = new Date(now.getFullYear(), now.getMonth() + 6, now.getDate());
  const displayMonthNum = year * 12 + month;
  const currentMonthNum = now.getFullYear() * 12 + now.getMonth();
  const maxMonthNum = maxDate.getFullYear() * 12 + maxDate.getMonth();
  const isPrevMonthDisabled = displayMonthNum <= currentMonthNum;
  const isNextMonthDisabled = displayMonthNum >= maxMonthNum;

  // Calculate price summary
const calculateTotal = () => {
  if (!selectedVenue || !selectedDates.from) {
    return { rent: 0, securityDeposit: 0, gst: 0, total: 0, days: 0, totalHours: 0 };
  }

  // ✅ For shooting venues with time slots - NO SECURITY DEPOSIT
  if (selectedVenue.hasTimingSlots && selectedTimeSlots.length > 0) {
    const totalRate = selectedTimeSlots.reduce((sum, slot) => sum + slot.rate, 0);
    const gst = totalRate * getGstRate(selectedVenue);
    const total = totalRate + gst;

    return {
      rent: totalRate,
      securityDeposit: 0,
      gst,
      total,
      days: 1,
      totalHours: selectedTimeSlots.reduce((sum, slot) => sum + slot.hours, 0)
    };
  }

  // ✅ For regular event venues
  const from = new Date(selectedDates.from);
  const to = selectedDates.to ? new Date(selectedDates.to) : from;
  const days = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  const rentAmount = selectedVenue.rate * days;
  
  // ✅ CRITICAL: Check if venue is Necklace Road (check both name and ID)
  const isNecklaceRoadEvent = 
    selectedVenue.id === "9eff7ae1-0d6d-4b68-813f-842d6c8c74bc" || // Exact ID match for event type
    (selectedVenue.name?.toLowerCase() === 'necklace road' && selectedVenue.type === 'event') ||
    (selectedVenue.name?.toLowerCase().includes('necklace road') && !selectedVenue.hasTimingSlots);
  
  // ✅ NO security deposit for Necklace Road event venue.
  // Otherwise the per-venue deposit published in `venue.metadata` — a flat
  // amount, or a percentage of the rent for venues that publish one.
  const securityDeposit = isNecklaceRoadEvent ? 0 : getSecurityDeposit(selectedVenue, rentAmount);

  const gst = rentAmount * getGstRate(selectedVenue);
  const totalAmount = rentAmount + securityDeposit + gst;

  console.log('🔍 Event Venue Calculation:', {
    venueName: selectedVenue.name,
    venueId: selectedVenue.id,
    venueType: selectedVenue.type,
    isNecklaceRoadEvent,
    rentAmount,
    securityDeposit,
    gst,
    totalAmount
  });

  return { 
    rent: rentAmount, 
    securityDeposit, 
    gst, 
    total: totalAmount,
    days
  };
};

  const costs = calculateTotal();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '24px',
        overflowY: 'auto'
      }}
      onClick={onClose}
    >
      <div
        data-availability-modal
        style={{
          background: theme.white,
          borderRadius: '24px',
          maxWidth: '1400px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: selectedVenue && selectedDates.from ? '1fr 380px' : '1fr'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Main Content */}
        <div>
          {/* Header */}
          <div
            style={{
              padding: '32px',
              borderBottom: `2px solid ${theme.gray100}`,
              background: `linear-gradient(135deg, ${theme.secondaryBlue}15, ${theme.primaryPink}05)`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div>
              <h2 style={{ fontSize: '2rem', fontWeight: '800', color: theme.gray900, marginBottom: '8px' }}>
                Pre-Wedding Shoot Booking
              </h2>
              <p style={{ color: theme.gray600, fontSize: '0.95rem' }}>
                Select venue, date, time slots and complete booking
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                background: theme.gray100,
                border: 'none',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={24} />
            </button>
          </div>

          <div style={{ padding: '32px' }}>
            {/* Step 1: Venue Selection */}
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '1.3rem', fontWeight: '800', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '32px', 
                  height: '32px', 
                  borderRadius: '50%', 
                  background: selectedVenue ? theme.success : theme.secondaryBlue,
                  color: theme.white,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.9rem',
                  fontWeight: '700'
                }}>
                  {selectedVenue ? '✓' : '1'}
                </div>
                Select Venue
              </h3>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '12px'
                }}
              >
                {venues.map((venue, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleVenueSelect(venue)}
                    style={{
                      padding: '16px',
                      background: selectedVenue?.name === venue.name ? `${venue.color}20` : theme.white,
                      border: `2px solid ${selectedVenue?.name === venue.name ? venue.color : theme.gray200}`,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s ease',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedVenue?.name !== venue.name) {
                        e.currentTarget.style.borderColor = venue.color;
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedVenue?.name !== venue.name) {
                        e.currentTarget.style.borderColor = theme.gray200;
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {selectedVenue?.name === venue.name && (
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: venue.color,
                        color: theme.white,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <CheckCircle size={16} />
                      </div>
                    )}
                    <div style={{ fontWeight: '700', fontSize: '0.95rem', marginBottom: '4px', color: theme.gray900 }}>
                      {venue.name}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: theme.gray600, marginBottom: '8px' }}>
                      {venue.location}
                    </div>
                    <div style={{ fontSize: '0.85rem', fontWeight: '700', color: venue.color }}>
                      ₹{venue.rate.toLocaleString()}/4 hrs
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Date Selection */}
            {selectedVenue && (
              <div style={{ marginBottom: '32px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '16px', 
                  flexWrap: 'wrap', 
                  gap: '12px' 
                }}>
                  <h3 style={{ 
                    fontSize: '1.3rem', 
                    fontWeight: '800', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px' 
                  }}>
                    <div style={{ 
                      width: '32px', 
                      height: '32px', 
                      borderRadius: '50%', 
                      background: selectedDates.from ? theme.success : theme.secondaryBlue,
                      color: theme.white,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.9rem',
                      fontWeight: '700'
                    }}>
                      {selectedDates.from ? '✓' : '2'}
                    </div>
                    Select Date
                  </h3>
                </div>

                {/* Calendar Section */}
                <div style={{
                  background: `${selectedVenue.color}08`,
                  padding: '24px',
                  borderRadius: '16px',
                  border: `2px solid ${selectedVenue.color}20`
                }}>
                  {/* Month Navigation */}
                  <div style={{ 
                    marginBottom: '16px', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                  }}>
                    <button
                      onClick={() => setCurrentMonth(new Date(year, month - 1))}
                      disabled={isPrevMonthDisabled}
                      style={{
                        background: theme.white,
                        border: `2px solid ${theme.gray200}`,
                        padding: '8px 16px',
                        borderRadius: '8px',
                        cursor: isPrevMonthDisabled ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        opacity: isPrevMonthDisabled ? 0.4 : 1
                      }}
                    >
                      <ChevronLeft size={16} />
                      Prev
                    </button>
                    <div style={{ fontSize: '1.1rem', fontWeight: '700' }}>
                      {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </div>
                    <button
                      onClick={() => setCurrentMonth(new Date(year, month + 1))}
                      disabled={isNextMonthDisabled}
                      style={{
                        background: theme.white,
                        border: `2px solid ${theme.gray200}`,
                        padding: '8px 16px',
                        borderRadius: '8px',
                        cursor: isNextMonthDisabled ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        opacity: isNextMonthDisabled ? 0.4 : 1
                      }}
                    >
                      Next
                      <ChevronRight size={16} />
                    </button>
                  </div>

                  {/* Loading State */}
                  {loadingBlockedDates && (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '40px', 
                      color: theme.gray600 
                    }}>
                      <div style={{ 
                        width: '40px', 
                        height: '40px', 
                        border: `4px solid ${theme.gray200}`,
                        borderTop: `4px solid ${selectedVenue.color}`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 16px'
                      }} />
                      Loading availability...
                    </div>
                  )}

                  {/* Error State */}
                  {blockedDatesError && (
                    <div style={{
                      padding: '16px',
                      background: `${theme.error}15`,
                      border: `2px solid ${theme.error}40`,
                      borderRadius: '12px',
                      marginBottom: '16px',
                      color: theme.error
                    }}>
                      Failed to load availability. Please try again.
                    </div>
                  )}

                  {/* Calendar Grid */}
                  {!loadingBlockedDates && (
                    <>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '8px',
                        textAlign: 'center'
                      }}>
                        {/* Day Headers */}
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                          <div
                            key={day}
                            style={{
                              fontWeight: '700',
                              fontSize: '0.85rem',
                              color: theme.gray700,
                              padding: '8px 0'
                            }}
                          >
                            {day}
                          </div>
                        ))}
                        
                        {/* Empty cells for days before month starts */}
                        {Array.from({ length: firstDay }, (_, i) => (
                          <div key={`empty-${i}`} />
                        ))}
                        
                        {/* Calendar days */}
                        {Array.from({ length: daysInMonth }, (_, i) => {
                          const day = i + 1;
                          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                          const isBlocked = blockedDates.has(dateStr);
                          const isSelected = isDateSelected(dateStr);
                          const isInTempRange = isDateInTempRange(dateStr);
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const isPast = new Date(year, month, day) < today;
                          const isBeyondLimit = new Date(year, month, day) > maxDate;
                          const isDisabled = isBlocked || isPast || isBeyondLimit;
                          const isToday = new Date().getDate() === day &&
                                         new Date().getMonth() === month &&
                                         new Date().getFullYear() === year;

                          return (
                            <button
                              key={day}
                              onClick={() => !isDisabled && handleDateClick(year, month, day)}
                              onMouseEnter={(e) => {
                                if (selectionMode === 'range' && selectedDates.from && !selectedDates.to && !isDisabled) {
                                  setTempHoverDate(dateStr);
                                } else if (!isDisabled && !isSelected && selectionMode === 'single') {
                                  e.currentTarget.style.background = `${selectedVenue.color}40`;
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                setTempHoverDate(null);
                                if (!isDisabled && !isSelected && selectionMode === 'single') {
                                  e.currentTarget.style.background = isToday ? `${selectedVenue.color}20` : theme.white;
                                  e.currentTarget.style.transform = 'scale(1)';
                                }
                              }}
                              disabled={isDisabled}
                              style={{
                                padding: '12px',
                                borderRadius: '8px',
                                background: isSelected
                                  ? selectedVenue.color
                                  : isInTempRange
                                  ? `${selectedVenue.color}40`
                                  : isBlocked
                                  ? theme.error
                                  : isPast || isBeyondLimit
                                  ? theme.gray100
                                  : isToday
                                  ? `${selectedVenue.color}20`
                                  : theme.white,
                                color: isSelected
                                  ? theme.white
                                  : isDisabled
                                  ? theme.white
                                  : theme.gray900,
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                fontWeight: isSelected || isToday ? '800' : '600',
                                fontSize: '0.9rem',
                                border: `2px solid ${isSelected ? selectedVenue.color : theme.gray200}`,
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {day}
                            </button>
                          );
                        })}
                      </div>

                      {/* Legend */}
                      <div style={{ 
                        marginTop: '16px', 
                        display: 'flex', 
                        gap: '16px', 
                        flexWrap: 'wrap', 
                        fontSize: '0.85rem' 
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ 
                            width: '16px', 
                            height: '16px', 
                            background: selectedVenue.color, 
                            borderRadius: '4px' 
                          }} />
                          <span>Selected</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ 
                            width: '16px', 
                            height: '16px', 
                            background: theme.error, 
                            borderRadius: '4px' 
                          }} />
                          <span>Booked</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ 
                            width: '16px', 
                            height: '16px', 
                            background: theme.gray100, 
                            borderRadius: '4px' 
                          }} />
                          <span>Past</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ 
                            width: '16px', 
                            height: '16px', 
                            background: theme.white, 
                            border: `2px solid ${theme.gray200}`, 
                            borderRadius: '4px' 
                          }} />
                          <span>Available</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Time Slot Selection - UPDATED WITH MULTIPLE SELECTION */}
            {selectedVenue?.hasTimingSlots && selectedDates.from && (
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ 
                  fontSize: '1.3rem', 
                  fontWeight: '800', 
                  marginBottom: '16px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px' 
                }}>
                  <div style={{ 
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '50%', 
                    background: selectedTimeSlots.length > 0 ? theme.success : theme.secondaryBlue,
                    color: theme.white,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.9rem',
                    fontWeight: '700'
                  }}>
                    {selectedTimeSlots.length > 0 ? '✓' : '3'}
                  </div>
                  Select Time Slots (Multiple Allowed)
                </h3>

                {/* Info Banner */}
                <div style={{
                  padding: '12px 16px',
                  background: `${theme.secondaryBlue}15`,
                  border: `2px solid ${theme.secondaryBlue}40`,
                  borderRadius: '12px',
                  marginBottom: '16px',
                  fontSize: '0.85rem',
                  color: theme.gray700
                }}>
                  <div style={{ fontWeight: '700', marginBottom: '4px', color: theme.secondaryBlue }}>
                    💡 Multiple Slot Booking Available
                  </div>
                  <div>
                    Select multiple 4-hour slots for extended shoots. Each slot: ₹4,000 + 18% GST
                  </div>
                </div>

                {/* Time Slots Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: '12px'
                }}>
                  {availableStartTimes.map((startTime, idx) => {
                    const endTime = addHoursToTime(startTime, 4);
                    const isSelected = selectedTimeSlots.some(s => s.startTime === startTime);

                    return (
                      <button
                        key={idx}
                        onClick={() => handleTimeSlotSelect(startTime)}
                        style={{
                          padding: '16px',
                          background: isSelected 
                            ? `linear-gradient(135deg, ${selectedVenue.color}, ${selectedVenue.color}dd)` 
                            : theme.white,
                          border: `2px solid ${isSelected ? selectedVenue.color : theme.gray200}`,
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          textAlign: 'center',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = selectedVenue.color;
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = `0 4px 12px ${selectedVenue.color}40`;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = theme.gray200;
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      >
                        {/* Selected Badge */}
                        {isSelected && (
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            background: theme.white,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <CheckCircle size={16} style={{ color: selectedVenue.color }} />
                          </div>
                        )}

                        <Clock 
                          size={20} 
                          style={{ 
                            margin: '0 auto 8px', 
                            color: isSelected ? theme.white : selectedVenue.color 
                          }} 
                        />
                        <div style={{ 
                          fontWeight: '700', 
                          fontSize: '0.95rem', 
                          color: isSelected ? theme.white : theme.gray900,
                          marginBottom: '4px'
                        }}>
                          {startTime} - {endTime}
                        </div>
                        <div style={{ 
                          fontSize: '0.75rem', 
                          color: isSelected ? `${theme.white}dd` : theme.gray600,
                          marginBottom: '8px'
                        }}>
                          4 hours
                        </div>
                        <div style={{ 
                          fontSize: '0.85rem', 
                          fontWeight: '700', 
                          color: isSelected ? theme.white : selectedVenue.color
                        }}>
₹{(selectedVenue.timingSlotConfig?.ratePerFourHours || 4000).toLocaleString('en-IN')}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Selected Slots Summary */}
                {selectedTimeSlots.length > 0 && (
                  <div style={{
                    marginTop: '16px',
                    padding: '16px',
                    background: `${selectedVenue.color}10`,
                    border: `2px solid ${selectedVenue.color}40`,
                    borderRadius: '12px'
                  }}>
                    <div style={{ 
                      fontWeight: '700', 
                      fontSize: '0.9rem', 
                      marginBottom: '12px',
                      color: theme.gray900
                    }}>
                      Selected Slots ({selectedTimeSlots.length})
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {selectedTimeSlots.map((slot, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '8px 12px',
                            background: theme.white,
                            border: `1px solid ${selectedVenue.color}60`,
                            borderRadius: '8px',
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <Clock size={14} style={{ color: selectedVenue.color }} />
                          <span style={{ fontWeight: '600' }}>
                            {slot.startTime} - {slot.endTime}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTimeSlots(selectedTimeSlots.filter((_, i) => i !== idx));
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '2px',
                              display: 'flex',
                              alignItems: 'center',
                              color: theme.gray600
                            }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div style={{ 
                      marginTop: '12px', 
                      fontSize: '0.85rem', 
                      color: theme.gray700,
                      display: 'flex',
                      justifyContent: 'space-between',
                      paddingTop: '12px',
                      borderTop: `1px solid ${selectedVenue.color}40`
                    }}>
                      <span>Total Duration:</span>
                      <span style={{ fontWeight: '700' }}>
                        {selectedTimeSlots.reduce((sum, slot) => sum + slot.hours, 0)} hours
                      </span>
                    </div>
                  </div>
                )}

                {formErrors.timeSlots && (
                  <p style={{ color: theme.error, fontSize: '0.85rem', marginTop: '8px' }}>
                    {formErrors.timeSlots}
                  </p>
                )}
              </div>
            )}

            {/* Step 4: Booking Details Form */}
            {selectedDates.from && (!selectedVenue?.hasTimingSlots || selectedTimeSlots.length > 0) && (
              <div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: '800', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ 
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '50%', 
                    background: theme.secondaryBlue,
                    color: theme.white,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.9rem',
                    fontWeight: '700'
                  }}>
                    {selectedVenue?.hasTimingSlots ? '4' : '3'}
                  </div>
                  Your Details
                </h3>

                <div style={{ display: 'grid', gap: '16px' }}>
                  {/* Event Name */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                      Event Name *
                    </label>
                    <input
                      type="text"
                      placeholder="E.g., Pre-wedding Photoshoot"
                      value={formData.eventName}
                      onChange={(e) => {
                        setFormData({ ...formData, eventName: e.target.value });
                        setFormErrors({ ...formErrors, eventName: undefined });
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: `2px solid ${formErrors.eventName ? theme.error : theme.gray200}`,
                        borderRadius: '8px',
                        fontSize: '0.95rem'
                      }}
                    />
                    {formErrors.eventName && (
                      <p style={{ color: theme.error, fontSize: '0.8rem', marginTop: '4px' }}>
                        {formErrors.eventName}
                      </p>
                    )}
                  </div>

                  {/* Applicant Name & Mobile */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                        Your Name *
                      </label>
                      <input
                        type="text"
                        placeholder="Enter full name"
                        value={formData.applicantName}
                        onChange={(e) => {
                          setFormData({ ...formData, applicantName: e.target.value });
                          setFormErrors({ ...formErrors, applicantName: undefined });
                        }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${formErrors.applicantName ? theme.error : theme.gray200}`,
                          borderRadius: '8px',
                          fontSize: '0.95rem'
                        }}
                      />
                      {formErrors.applicantName && (
                        <p style={{ color: theme.error, fontSize: '0.8rem', marginTop: '4px' }}>
                          {formErrors.applicantName}
                        </p>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                        Mobile Number *
                      </label>
                      <input
                        type="tel"
                        placeholder="Enter 10-digit mobile"
                        value={formData.mobile}
                        onChange={(e) => {
                          setFormData({ ...formData, mobile: e.target.value });
                          setFormErrors({ ...formErrors, mobile: undefined });
                        }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${formErrors.mobile ? theme.error : theme.gray200}`,
                          borderRadius: '8px',
                          fontSize: '0.95rem'
                        }}
                      />
                      {formErrors.mobile && (
                        <p style={{ color: theme.error, fontSize: '0.8rem', marginTop: '4px' }}>
                          {formErrors.mobile}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Email & GST */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                        Email *
                      </label>
                      <input
                        type="email"
                        placeholder="Enter email address"
                        value={formData.email}
                        onChange={(e) => {
                          setFormData({ ...formData, email: e.target.value });
                          setFormErrors({ ...formErrors, email: undefined });
                        }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${formErrors.email ? theme.error : theme.gray200}`,
                          borderRadius: '8px',
                          fontSize: '0.95rem'
                        }}
                      />
                      {formErrors.email && (
                        <p style={{ color: theme.error, fontSize: '0.8rem', marginTop: '4px' }}>
                          {formErrors.email}
                        </p>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                        GST Number (Optional)
                      </label>
                      <input
                        type="text"
                        placeholder="Enter GST if applicable"
                        value={formData.gstNumber}
                        onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${theme.gray200}`,
                          borderRadius: '8px',
                          fontSize: '0.95rem'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}


{/* ✅ NEW: TERMS AND CONDITIONS ACCEPTANCE SECTION */}
{selectedDates.from && (!selectedVenue?.hasTimingSlots || selectedTimeSlots.length > 0) && (
  <div style={{
    background: `${theme.secondaryBlue}08`,
    padding: '20px',
    borderRadius: '16px',
    border: `2px solid ${termsAccepted ? theme.success : formErrors.termsAccepted ? theme.error : theme.secondaryBlue}40`,
    marginTop: '24px'
  }}>
    <h3 style={{
      fontSize: '1.1rem',
      fontWeight: '700',
      color: theme.gray900,
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }}>
      <FileText size={20} style={{ color: theme.secondaryBlue }} />
      Terms & Conditions
    </h3>

    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      marginBottom: '12px'
    }}>
      <button
        onClick={() => {
          setTermsAccepted(!termsAccepted);
          setFormErrors({ ...formErrors, termsAccepted: undefined });
        }}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0
        }}
      >
        {termsAccepted ? (
          <CheckSquare size={24} style={{ color: theme.success }} />
        ) : (
          <CheckSquare size={24} style={{ color: theme.gray400 }} />
        )}
      </button>
      <div style={{ fontSize: '0.95rem', lineHeight: 1.6, color: theme.gray700 }}>
        I have read and agree to the{' '}
        <button
          onClick={() => setShowTermsModal(true)}
          style={{
            background: 'none',
            border: 'none',
            color: theme.secondaryBlue,
            fontWeight: '700',
            textDecoration: 'underline',
            cursor: 'pointer',
            padding: 0,
            fontSize: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          Terms and Conditions
        </button>
        {' '}for shooting at {selectedVenue?.name}.
      </div>
    </div>

    {formErrors.termsAccepted && (
      <div style={{
        padding: '12px',
        background: `${theme.error}15`,
        border: `2px solid ${theme.error}40`,
        borderRadius: '8px',
        fontSize: '0.85rem',
        color: theme.error,
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <Info size={16} />
        {formErrors.termsAccepted}
      </div>
    )}

    {termsAccepted && (
      <div style={{
        padding: '12px',
        background: `${theme.success}15`,
        border: `2px solid ${theme.success}40`,
        borderRadius: '8px',
        fontSize: '0.85rem',
        color: theme.success,
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <CheckSquare size={16} />
        Terms and conditions accepted
      </div>
    )}
  </div>
)}
            {!selectedVenue && (
              <div
                style={{
                  padding: '60px 20px',
                  textAlign: 'center',
                  color: theme.gray600,
                  background: theme.gray50,
                  borderRadius: '12px'
                }}
              >
                <Calendar size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                <p style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                  Please select a venue to check availability
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Booking Summary Sidebar */}
        {selectedVenue && selectedDates.from && (!selectedVenue.hasTimingSlots || selectedTimeSlots.length > 0) && (
       <div style={{
  background: `linear-gradient(135deg, ${theme.gray50}, ${theme.white})`,
  borderLeft: `2px solid ${theme.gray100}`,
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end'  // ✅ ADD THIS LINE
}}>
            <h3 style={{ fontSize: '1.3rem', fontWeight: '800', marginBottom: '20px', color: theme.gray900 }}>
              Booking Summary
            </h3>

            {/* Venue Details */}
            <div style={{
              background: theme.white,
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px',
              border: `2px solid ${selectedVenue.color}40`
            }}>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '0.75rem', color: theme.gray600, marginBottom: '4px' }}>Selected Venue</div>
                <div style={{ fontWeight: '700', color: theme.gray900, fontSize: '1.1rem' }}>{selectedVenue.name}</div>
                <div style={{ fontSize: '0.85rem', color: theme.gray600 }}>{selectedVenue.location}</div>
              </div>
            </div>

            {/* Date & Time Details */}
            <div style={{
              background: theme.white,
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px',
              border: `2px solid ${theme.gray200}`
            }}>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '0.75rem', color: theme.gray600, marginBottom: '4px' }}>Shoot Date</div>
                <div style={{ fontWeight: '600', color: theme.gray900 }}>
                  {new Date(selectedDates.from).toLocaleDateString('en-IN', { 
                    weekday: 'long',
                    month: 'long', 
                    day: 'numeric',
                    year: 'numeric' 
                  })}
                </div>
              </div>

              {/* Time Slots */}
              {selectedVenue.hasTimingSlots && selectedTimeSlots.length > 0 && (
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${theme.gray200}` }}>
                  <div style={{ fontSize: '0.75rem', color: theme.gray600, marginBottom: '8px' }}>Selected Time Slots</div>
                  {selectedTimeSlots.map((slot, idx) => (
                    <div key={idx} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '6px',
                      padding: '8px',
                      background: `${selectedVenue.color}10`,
                      borderRadius: '6px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Clock size={14} style={{ color: selectedVenue.color }} />
                        <span style={{ fontSize: '0.85rem', fontWeight: '600' }}>
                          {slot.startTime} - {slot.endTime}
                        </span>
                      </div>
                      <span style={{ fontSize: '0.85rem', fontWeight: '700', color: selectedVenue.color }}>
                        ₹{slot.rate.toLocaleString()}
                      </span>
                    </div>
                  ))}
                  <div style={{ fontSize: '0.75rem', color: theme.gray600, marginTop: '8px' }}>
                    Total: {costs.totalHours} hours
                  </div>
                </div>
              )}
            </div>

            {/* Price Breakdown */}
            <div style={{
              background: `linear-gradient(135deg, ${selectedVenue.color}10, ${selectedVenue.color}05)`,
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              border: `2px solid ${selectedVenue.color}30`
            }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: theme.gray700 }}>
                    {selectedVenue.hasTimingSlots ? 'Shooting Charges' : `Venue Rent (${costs.days} ${costs.days === 1 ? 'day' : 'days'})`}
                  </span>
                  <span style={{ fontWeight: '600' }}>{formatCurrency(costs.rent)}</span>
                </div>
                
                {/* Only show security deposit for event venues */}
                {!selectedVenue.hasTimingSlots && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: theme.gray700 }}>Security Deposit</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(costs.securityDeposit)}</span>
                  </div>
                )}
                
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ color: theme.gray700 }}>GST @ 18%</span>
                  <span style={{ fontWeight: '600' }}>{formatCurrency(costs.gst)}</span>
                </div>
                <div style={{
                  borderTop: `2px solid ${selectedVenue.color}40`,
                  paddingTop: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: '700', fontSize: '1.1rem', color: theme.gray900 }}>Total Amount</span>
                  <span style={{ fontWeight: '800', fontSize: '1.3rem', color: selectedVenue.color }}>
                    {formatCurrency(costs.total)}
                  </span>
                </div>
              </div>
            </div>

            <div style={{
              background: `${theme.warning}15`,
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              fontSize: '0.75rem',
              color: theme.gray700,
              border: `1px solid ${theme.warning}40`
            }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span style={{ fontWeight: '600' }}>
                  {selectedVenue.hasTimingSlots ? 'Shooting Venue Pricing' : 'Full payment required'}
                </span>
              </div>
              <div style={{ paddingLeft: '22px' }}>
                {selectedVenue.hasTimingSlots ? (
                  <>
                    • ₹4,000 per 4-hour slot<br/>
                    • Multiple slots can be booked<br/>
                    • 18% GST applicable<br/>
                    • No security deposit required
                  </>
                ) : (
                  'Security deposit will be refunded after venue inspection'
                )}
              </div>
            </div>

            <button
              onClick={handleProceedToPayment}
              disabled={isProcessingPayment || !selectedDates.from || (selectedVenue.hasTimingSlots && selectedTimeSlots.length === 0)}
              style={{
                width: '100%',
                background: isProcessingPayment || !selectedDates.from || (selectedVenue.hasTimingSlots && selectedTimeSlots.length === 0)
                  ? theme.gray300
                  : `linear-gradient(135deg, ${selectedVenue.color}, ${selectedVenue.color}dd)`,
                color: theme.white,
                border: 'none',
                padding: '16px',
                borderRadius: '12px',
                fontSize: '1.05rem',
                fontWeight: '700',
                cursor: isProcessingPayment || !selectedDates.from || (selectedVenue.hasTimingSlots && selectedTimeSlots.length === 0) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginTop: 'auto',
                opacity: isProcessingPayment ? 0.6 : 1
              }}
            >
              {isProcessingPayment ? (
                <>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '3px solid white',
                    borderTop: '3px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard size={20} />
                  Proceed to Payment
                </>
              )}
            </button>
          </div>
        )}
      </div>


      {/* ✅ NEW: TERMS AND CONDITIONS MODAL */}
{showTermsModal && selectedVenue && (
  <div
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10001,
      padding: '24px',
      overflowY: 'auto'
    }}
    onClick={() => setShowTermsModal(false)}
  >
    <div
      style={{
        background: theme.white,
        borderRadius: '20px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '85vh',
        overflowY: 'auto',
        position: 'relative'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Terms Header */}
      <div style={{
        padding: '24px 32px',
        borderBottom: `2px solid ${theme.gray100}`,
        background: `linear-gradient(135deg, ${theme.secondaryBlue}15, ${theme.secondaryBlue}05)`,
        position: 'sticky',
        top: 0,
        zIndex: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h2 style={{
            fontSize: '1.8rem',
            fontWeight: '800',
            color: theme.gray900,
            marginBottom: '4px'
          }}>
            Terms & Conditions
          </h2>
          <p style={{ color: theme.gray600, fontSize: '0.9rem' }}>
            {selectedVenue.name} - Shooting Location
          </p>
        </div>
        <button
          onClick={() => setShowTermsModal(false)}
          style={{
            background: theme.gray100,
            border: 'none',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.gray700
          }}
        >
          <X size={24} />
        </button>
      </div>

      {/* Terms Content */}
      <div style={{ padding: '32px' }}>
        {selectedVenue.termsAndConditions && selectedVenue.termsAndConditions.length > 0 ? (
          <ol style={{
            listStyleType: 'decimal',
            paddingLeft: '24px',
            color: theme.gray800,
            fontSize: '0.95rem',
            lineHeight: 1.8
          }}>
            {selectedVenue.termsAndConditions.map((term: string, index: number) => (
              <li key={index} style={{
                marginBottom: '16px',
                paddingLeft: '8px'
              }}>
                {term}
              </li>
            ))}
          </ol>
        ) : (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: theme.gray500
          }}>
            <FileText size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
            <p style={{ fontSize: '1rem', fontWeight: '600' }}>
              No specific terms available for this venue
            </p>
            <p style={{ fontSize: '0.9rem', marginTop: '8px' }}>
              Standard HMDA booking terms and conditions apply
            </p>
          </div>
        )}

        {/* Footer Note */}
        <div style={{
          marginTop: '32px',
          padding: '16px',
          background: `${theme.info}10`,
          border: `2px solid ${theme.info}40`,
          borderRadius: '12px',
          fontSize: '0.85rem',
          color: theme.gray700,
          lineHeight: 1.6
        }}>
          <strong style={{ color: theme.info }}>Note:</strong> By proceeding with the booking, you acknowledge that you have read and understood all terms and conditions listed above.
        </div>
      </div>

      {/* Terms Footer Actions */}
      <div style={{
        padding: '20px 32px',
        borderTop: `2px solid ${theme.gray100}`,
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        position: 'sticky',
        bottom: 0,
        background: theme.white
      }}>
        <button
          onClick={() => setShowTermsModal(false)}
          style={{
            padding: '12px 24px',
            background: theme.white,
            color: theme.gray700,
            border: `2px solid ${theme.gray200}`,
            borderRadius: '8px',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Close
        </button>
        <button
          onClick={() => {
            setTermsAccepted(true);
            setShowTermsModal(false);
            setFormErrors({ ...formErrors, termsAccepted: undefined });
          }}
          style={{
            padding: '12px 24px',
            background: `linear-gradient(135deg, ${theme.secondaryBlue}, ${theme.govBlue})`,
            color: theme.white,
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <CheckSquare size={18} />
          Accept & Continue
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default AvailabilityModal;